
import { Prop } from 'vue-property-decorator';
import { State } from 'vuex-class';
import { Validation } from 'vuelidate';
import ordinal from 'ordinal';
import Vue from 'vue';

import Location from '@/services/Location';
import { Hours } from '@/services/Location';

import ListActions from '@/components/DraggableCardList/ListActions';

interface HoursValidation extends Validation {
	$each: Array<Validation & { [key: string]: unknown }>;
}

export default abstract class HoursSection extends Vue {
	@State('location') location!: Location;
	@Prop({}) readonly validation!: HoursValidation;
	
	/**
	 * Key on location
	 * @example "lobbyHours"
	 * @example "atmHours"
	 * @example "driveThroughHours"
	 */
	abstract hoursType: keyof Location;

	/**
	 * A blank item with default values
	 */
	readonly blank: Hours = {
		'name': '',
		'value': '',
	};

	lastTouched = -1;

	protected getCardTitle(index: number): string {
		return ordinal(index + 1);
	}

	protected dereference(list: Array<Hours>): Array<Hours> {
		return list.map((a: Hours): Hours => {
			return {...a} as Hours;
		});
	}

	get hoursList(): Array<Hours> {
		// this.preventEmptyList();
		return this.location[this.hoursType] as Array<Hours>;
	}

	set hoursList(list: Array<Hours>) {
		this.$set(this.location, this.hoursType, list);
	}


	/**
	 * Mustache-formatted error messages for this hours-list
	 */
	getValidationErrors(index: number, key: string): Array<string> {
		const item = this.validation?.$each?.[index]?.[key] as Validation;

		if (!item || !item.$invalid || !item.$dirty) {
			return [];
		}

		const errors = [];

		if ('required' in item.$params) {
			errors.push('Please complete this field.');
		}
		
		// well, *something* is wrong with it
		// fallback for anything more specific
		if (!errors.length) {
			errors.push('Invalid entry');
		}

		return errors;
	}

	touch(index: number, key: string) {
		const item = this.validation?.$each?.[index]?.[key] as Validation;
		item?.$touch();
	}

	resetItem(index: number): void {
		if (!this.validation.$each[index]) {
			return;
		}
		this.validation.$each[index].$reset();
	}

	touchItem(index: number): void {
		if (!this.validation.$each[index]) {
			return;
		}
		this.validation.$each[index].$touch();
	}

	updateLastTouched(i: number) {
		this.lastTouched = i;
	}

	getLastTouched() {
		return this.lastTouched;
	}

	/**
	 * Add a blank item to this hours list
	 */
	add() {
		this.hoursList = ListActions.add(this.hoursList, this.blank) as Array<Hours>;
		this.resetItem(this.hoursList.length - 1);
	}

	/**
	 * Move an existing item in this hours list
	 */
	move(fromIndex: number, toIndex: number) {
		this.hoursList = ListActions.move(this.hoursList, fromIndex, toIndex) as Array<Hours>;

		const min = Math.min(fromIndex, toIndex);
		const max = Math.max(fromIndex, toIndex);

		// items between the start and end point have shifted over
		// revalidate all positions

		let i;
		for (i = min; i <= max; i++) {
			this.touchItem(i);
		}
	}

	/**
	 * Delete an existing item from this hours list
	 * (Note: "delete" is a reserved word in JS)
	 */
	remove(index: number) {
		this.resetItem(index); // before operation
		this.hoursList = ListActions.remove(this.hoursList, index) as Array<Hours>;
		this.touchItem(index); // touch whatever's there afterward
		this.preventEmptyList();
	}

	preventEmptyList(hoursType: string | null = null) {
		const key = (hoursType || this.hoursType) as keyof Location;
		if ((this.location[key] as Array<Hours>).length === 0) {
			(this.location[key] as Array<Hours>).push({...this.blank});
		}
	}
}
